import rectangleState from './shapes/rectangle';
import roundedRectangleState from './shapes/rounded-rectangle';
import circleState from './shapes/circle';
import halfCircleState from './shapes/half-circle';
import quarterCircleState from './shapes/quarter-circle';
import eggState from './shapes/egg';
import ellipseState from './shapes/ellipse';
import flatOvalState from './shapes/flat-oval';
import slopedRectangleState from './shapes/sloped-rectangle';
import slopedRectangleFlipHorizontalState from './shapes/sloped-rectangle-flip-horizontal';
import slopedRightRectangleState from './shapes/sloped-right-rectangle';
import slopedRightRectangleFlipVerticalState from './shapes/sloped-right-rectangle-flip-vertical';
import archedRectangleState from './shapes/arched-rectangle';
import isoscelesTriangleState from './shapes/isosceles-triangle';
import rightAngledTriangleState from './shapes/right-angled-triangle';
import rightAngledTriangleMirroredState from './shapes/right-angled-triangle-mirrored';
import trapezoidState from './shapes/trapezoid';
import hexagonState from './shapes/hexagon';
import octagonState from './shapes/octagon';
import polygonState from './shapes/polygon';
import heartState from './shapes/heart';
import starState from './shapes/star';
import arrowState from './shapes/arrow';
import kiteState from './shapes/kite';
import barrelState from './shapes/barrel';
import jewelState from './shapes/jewel';
import textState from './shapes/text';
import dxfState from './shapes/dxf';
import dropletState from './shapes/droplet';
import parallogramState from './shapes/parallelogram';
import parallogramFlipHorizontalState from './shapes/parallelogram-flip-horizontal';

import isEmpty from "lodash/isEmpty";
import AbstractShape from "@/Models/Shapes/AbstractShape";
import Length from "../../../../source/js/shared/models/Geometry/Length";
import Rectangle from "../../../../source/js/shared/models/Geometry/Rectangle";
import ShapeConfigurationFactory from "@/Models/ShapeConfigurations/ShapeConfigurationFactory";
import { ShapeConfigurationInterface } from "@/Models/ShapeConfigurations/ShapeConfigurationInterface";
import makerjs, { IMeasureWithCenter } from "makerjs";
import { ShapeModelDecorator } from '@/utilities/decorators/PreviewModelDecorator';
import {PreviewShapeFactory} from "@/Models/Shapes/PreviewShapeFactory";
import {Fallback} from "@/Models/Shapes/Fallback";

export default {
	modules: {
		'rectangle': rectangleState,
		'rounded-rectangle': roundedRectangleState,
		'circle': circleState,
		'half-circle': halfCircleState,
		'quarter-circle': quarterCircleState,
		'egg': eggState,
		'ellipse': ellipseState,
		'flat-oval': flatOvalState,
		'sloped-rectangle': slopedRectangleState,
		'sloped-rectangle-flip-horizontal': slopedRectangleFlipHorizontalState,
		'sloped-right-rectangle': slopedRightRectangleState,
		'sloped-right-rectangle-flip-vertical': slopedRightRectangleFlipVerticalState,
		'arched-rectangle': archedRectangleState,
		'isosceles-triangle': isoscelesTriangleState,
		'right-angled-triangle': rightAngledTriangleState,
		'right-angled-triangle-mirrored': rightAngledTriangleMirroredState,
		'trapezoid': trapezoidState,
		'hexagon': hexagonState,
		'octagon': octagonState,
		'polygon': polygonState,
		'heart': heartState,
		'star': starState,
		'arrow': arrowState,
		'kite': kiteState,
		'barrel': barrelState,
		'jewel': jewelState,
		'text': textState,
		'dxf': dxfState,
		'droplet': dropletState,
		'parallelogram': parallogramState,
		'parallelogram-flip-horizontal': parallogramFlipHorizontalState,
	},
	getters: {
		enclosingRectangle( state, getters, rootState, rootGetter ): IMeasureWithCenter {
			if (getters.shape === 'text') {
				return makerjs.measure.modelExtents( getters['text/textModel'] );
			}

			return makerjs.measure.modelExtents( rootGetter.model );
		},
		getMinimalSize: ( state, getters, rootState, rootGetters ): number => {
			if (isEmpty( getters.shape ) || !( getters.shape in state )) {
				return 0;
			}

			const minimums = window.measurementSelectorVariables.shapeSettings['default']['minimumDimension'];

			return minimums[rootGetters.activeEdgeFinish];
		},
		baseShapeInstance: ( state, getters ): AbstractShape => {
			if (!getters.shape || !( getters.shape in state )) {
				return null;
			}

			return getters[`${getters.shape}/getInstance`];
		},
		shapeInstance: ( state, getters ): Function => {
			return ( shape: string, measurements: object ): AbstractShape => {
				const config = { ...measurements, shape };

				if ( shape === 'text' ) {
					const { fontIsLoaded, getSelectedFontName, loadedFonts } = getters;

					// Don't create model if font isn't present
					if ( ! fontIsLoaded( getSelectedFontName ) ) {
						return new Fallback();
					}

					config['fonts'] = loadedFonts;
				}

				return ( new PreviewShapeFactory() ).get( config );
			};
		},
		getShapeArea( state, getters ): number {
			if (!( getters.shape in state )) {
				return 0;
			}

			return getters[`${getters.shape}/getArea`];
		},
		getConfiguration(): Function {
			return ( shape: string ): ShapeConfigurationInterface => {
				return ( new ShapeConfigurationFactory() ).get_configuration( shape );
			};
		},
		calculatedMaximums( state, getters, rootState, rootGetters ): Function {
			return ( maximumLength: number, maximumWidth: number, materialLoss: number ): Record<string, Length | number> => {
				const { canUseFullPlateLength, canUseFullPlateWidth } = window.measurementSelectorVariables.shapeSettings;

				// Tweak maximum values based on sawing method.
				const usesSawing = rootGetters.activeEdgeFinish === 'sawing';

				if ( ! usesSawing ) {
					maximumLength = canUseFullPlateLength ? maximumLength : maximumLength - 40;
					maximumWidth = canUseFullPlateWidth ? maximumWidth : maximumWidth - 40;
				}

				let maximumRectangle = new Rectangle( new Length( maximumLength ), new Length( maximumWidth ) );

				if ('rectangle' !== getters.shape) {
					maximumRectangle = maximumRectangle.remove_sides( new Length( materialLoss ) );
				}

				return getters.getConfiguration( getters.shape ).get_maximum_dimensions( maximumRectangle, getters.dimensions );
			};
		},
		maximums( state, getters ): Record<string, Length | number> {
			let maximumLength = window.measurementSelectorVariables.shapeSettings.maximumLength;
			let maximumWidth = window.measurementSelectorVariables.shapeSettings.maximumWidth;

			// Swap maximum values for certain shapes.
			if( getters.getPricingGroup.swapMaximumDimensions( getters.shape ) ) {
				maximumLength = window.measurementSelectorVariables.shapeSettings.maximumWidth;
				maximumWidth = window.measurementSelectorVariables.shapeSettings.maximumLength;
			}

			return getters.calculatedMaximums(
				maximumLength,
				maximumWidth,
				window.measurementSelectorVariables.settings.standardMaterialLoss
			);
		},
		getCenter(state, getters, rootState, { getShapeInstance }): (roundNumber?: boolean, unit?: string) => { x: number, y: number } {
			return ( roundNumber = true, unit = 'mm' ): { x: number, y: number } => {
				const modelDecorator = new ShapeModelDecorator(null, getShapeInstance);
				const model = modelDecorator.getModel();
				const measure = makerjs.measure.modelExtents(model);
				let centerX = 0;
				let centerY = 0;

				// Some shapes are drawn from the center, so we need to adjust the center point.
				if(measure['low'][0] === 0 && measure['low'][1] === 0) {
					centerX = measure['center'][0];
					centerY = measure['center'][1];
				} else {
					centerX = measure['low'][0] * -1;
					centerY = measure['low'][1] * -1;
				}

				let offsetX = new Length(centerX);
				let offsetY = new Length(centerY);

				if ( roundNumber ) {
					offsetX = offsetX.round( unit !== 'cm' ? 0 : 2 );
					offsetY = offsetY.round( unit !== 'cm' ? 0 : 2 );
				}

				return {
					x: offsetX[`in_${unit}`](),
					y: offsetY[`in_${unit}`](),
				};
			};
		},
		getMeasurements( state, getters, rootState, { dimensions, cutoutDimensions } ): Function {
			return ( shape: string, isCutout = false ): ShapeConfigurationInterface => {
				return isCutout ? cutoutDimensions : dimensions;
			};
		},
		getModifiers( state, getters, rootState, { modifiers, cutoutModifiers } ): Function {
			return ( isCutout= false ): ShapeConfigurationInterface => {
				return isCutout ? cutoutModifiers : modifiers;
			};
		},
		isShapeValid( state, getters, rootState, { isDimensionValid, isCutoutValid } ): Function {
			return ( isCutout = false ): ShapeConfigurationInterface => {
				return isCutout ? isCutoutValid : isDimensionValid;
			};
		},
		getCategorizedShapes(state, getters): Function {
			return ( shapes ): object => {
				const categories = {};

				shapes.forEach((shape) => {
					const configuration = getters.getConfiguration(shape);
					const category = configuration.get_category();

					if (category === "") {
						return;
					}

					if (!(category in categories)) {
						categories[category] = [];
					}

					categories[category].push(shape);
				});

				return categories;
			};
		}
	},
};
